import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { GATED_BIBLIOGRAPHY_ON_CITATION_COUNT_MODAL } from '@chegg-wt/core/state/modals/constants'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { AppState } from '@chegg-wt/core/state'
import { modalDataSelector } from '@chegg-wt/core/state/modals/selectors'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { GatedBibliographyOnCitationCountModalData } from '@chegg-wt/core/state/bibliography/types'
import { Config } from '@chegg-wt/core/config'
import { CITATION_LIST_NAME_LOWER_SINGLE } from '@chegg-wt/core/constants/citations'
import {
  emitComponentViewEvent,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import { componentOrigins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import BaseGatedBibliographyModal from '../BaseGatedBibliographyModal'
import { GatedBibliographyOnCitationCountModalProps } from './types'

export const GatedBibliographyOnCitationCountModal: React.FC<GatedBibliographyOnCitationCountModalProps> = ({
  data,
  navigated,
  limitApproachingModalOpen,
  limitReachedModalOpen,
  limitApproachingContinueRioEvent,
  limitApproachingUpgradeRioEvent,
  limitReachedUpgradeRioEvent,
}) => {
  const { toSourceTypeSelection, toUpgrade } = useRoutesContext()
  const [isRioSent, setIsRioSent] = useState<boolean>(false)
  const { blockThreshold, isWarningOnly, currentStyle } = data
  const premiumAccountCopy = `${Config.siteName} ${Config.premiumAccountCopy}`
  const header = 'Create a new citation'
  const message = isWarningOnly
    ? `You're getting close to the reference limit for free accounts. Please upgrade to a ${premiumAccountCopy} account to add more than ${blockThreshold} references.`
    : `Free accounts can add up to ${blockThreshold} references to a ${CITATION_LIST_NAME_LOWER_SINGLE}. Please upgrade to a ${premiumAccountCopy} account to add more.`

  useEffect(() => {
    if (!isRioSent) {
      if (isWarningOnly) {
        limitApproachingModalOpen(header)
      } else {
        limitReachedModalOpen(header)
      }
      setIsRioSent(true)
    }
  }, [isWarningOnly, isRioSent])

  const onUpgrade = (text: string) => {
    if (isWarningOnly) {
      limitApproachingUpgradeRioEvent(text)
    } else {
      limitReachedUpgradeRioEvent(text)
    }
    navigated(toUpgrade())
  }

  const onContinue = isWarningOnly
    ? (text: string) => {
        limitApproachingContinueRioEvent(text)
        navigated(toSourceTypeSelection({ style: currentStyle }))
      }
    : null

  return (
    <BaseGatedBibliographyModal
      modalName={GATED_BIBLIOGRAPHY_ON_CITATION_COUNT_MODAL}
      header={header}
      message={message}
      isDismissible
      onContinue={onContinue}
      onUpgrade={onUpgrade}
    />
  )
}

const mapStateToProps = (state: AppState) => ({
  data: modalDataSelector(state) as GatedBibliographyOnCitationCountModalData,
})

const mapDispatchToProps = {
  navigated,
  limitApproachingModalOpen: (text: string) =>
    emitComponentViewEvent({
      origin: componentOrigins.citationLimitApproaching,
      text,
    }),
  limitReachedModalOpen: (text: string) =>
    emitComponentViewEvent({
      origin: componentOrigins.citationLimitReached,
      text,
    }),
  limitApproachingContinueRioEvent: (text: string) =>
    userClick({ origin: origins.citationLimitApproachingContinueCTA, text }),
  limitApproachingUpgradeRioEvent: (text: string) =>
    userClick({ origin: origins.citationLimitApproachingUpgradeCTA, text }),
  limitReachedUpgradeRioEvent: (text: string) =>
    userClick({ origin: origins.citationLimitReachedUpgradeCTA, text }),
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GatedBibliographyOnCitationCountModal)
